import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Button, Dialog, TEMPLATE_EMOJIS, tripColorFor, tripHueTint, vars } from "@pack-it-up/ui-web";
import type {
  AllRepositories,
  AssociatedRow,
  ImportPlan,
  TemplateDeletePreview,
  TemplateInput,
  TemplateRemovalPlan,
  TemplateSummary,
} from "@pack-it-up/core";
import { planTemplateRemoval, previewTemplateDelete } from "@pack-it-up/core";
import type { TemplateId, TripId, UserId } from "@pack-it-up/shared";
import {
  createImportPickerStore,
  createTemplateRefreshStore,
  refreshCandidates,
  syncCandidates,
  type createTripDetailStore,
} from "@pack-it-up/viewmodel";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useSession } from "../../app/SessionProvider.js";
import { useSessionDeps } from "../../app/sessionDeps.js";
import { useStores } from "../../app/AppContext.js";
import { EyeIcon, RefreshDownIcon, SearchIcon, SyncIcon, TrashIcon } from "../../app/icons.js";
import { RefreshTemplateDialog } from "./RefreshTemplateDialog.js";
import type { Trip } from "@pack-it-up/core";

// ---------------------------------------------------------------------------
// Inline style helpers — mirrors design-system tokens
// ---------------------------------------------------------------------------

const searchWrapStyle: CSSProperties = {
  padding: `${vars.space.md} ${vars.space.lg} 0`,
};

const searchBoxStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,
  background: vars.color.surfaceRaised,
  border: `1px solid ${vars.color.border}`,
  borderRadius: vars.radius.sm,
  padding: `${vars.space.sm} ${vars.space.md}`,
  color: vars.color.textMuted,
};

const searchInputStyle: CSSProperties = {
  flex: 1,
  background: "none",
  border: "none",
  outline: "none",
  color: vars.color.text,
  fontFamily: vars.font.body,
  fontSize: vars.font.sizeSm,
};

const dividerStyle: CSSProperties = {
  border: "none",
  borderTop: `1px solid ${vars.color.border}`,
  margin: `${vars.space.sm} ${vars.space.sm}`,
};

const emptyStyle: CSSProperties = {
  padding: `${vars.space.xl} ${vars.space.sm}`,
  textAlign: "center",
  color: vars.color.textMuted,
  fontSize: vars.font.sizeSm,
};

const rowStyle = (hovered: boolean): CSSProperties => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  padding: `10px ${vars.space.sm}`,
  borderRadius: vars.radius.md,
  backgroundColor: hovered ? vars.color.surfaceRaised : "transparent",
  transition: "background-color 0.1s",
});

// The row's top line: icon, name, preview eye, and (unapplied only) the Add button.
const primaryLineStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: vars.space.md,
};

// Issue: an applied row's Refresh/Sync/Remove actions used to crowd the top line
// horizontally, overlapping the name at the modal's width. They now sit on their own
// line under the name, indented to line up under it (icon width + the row's gap).
const secondaryLineStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "4px",
  marginLeft: `calc(38px + ${vars.space.md})`,
};

// Tinted with the template's color (Doc 07 §2) — falls back to a stable per-id hue.
const iconStyle = (template: TemplateSummary): CSSProperties => ({
  width: "38px",
  height: "38px",
  borderRadius: vars.radius.md,
  background: tripHueTint[template.color ?? tripColorFor(template.id)],
  border: `1px solid ${vars.color.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  flexShrink: 0,
});

// A ghost row's template doc is gone, so there's no color to tint with — a plain
// muted square instead.
const ghostIconStyle: CSSProperties = {
  width: "38px",
  height: "38px",
  borderRadius: vars.radius.md,
  background: vars.color.surfaceRaised,
  border: `1px solid ${vars.color.border}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: vars.color.textMuted,
  flexShrink: 0,
};

const ghostSublabelStyle: CSSProperties = {
  fontSize: vars.font.size2xs,
  color: vars.color.textMuted,
  marginTop: "1px",
};

const nameStyle: CSSProperties = {
  fontSize: vars.font.sizeSm,
  fontWeight: vars.font.weightMedium,
  color: vars.color.text,
};

const previewBtnStyle = (hovered: boolean): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "30px",
  height: "30px",
  borderRadius: "7px",
  border: `1px solid ${vars.color.border}`,
  background: hovered ? vars.color.surfaceRaised : "transparent",
  color: hovered ? vars.color.text : vars.color.textMuted,
  cursor: "pointer",
  flexShrink: 0,
  padding: 0,
});

// T28 §4.1 — the "Sync back to template…" action, a quiet text button beside the preview eye.
const syncBtnStyle = (hovered: boolean): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: vars.space.xs,
  fontSize: vars.font.size2xs,
  fontWeight: vars.font.weightMedium,
  padding: "5px 10px",
  borderRadius: "7px",
  border: `1px solid ${vars.color.border}`,
  background: hovered ? vars.color.surfaceRaised : "transparent",
  color: hovered ? vars.color.text : vars.color.textMuted,
  cursor: "pointer",
  flexShrink: 0,
  fontFamily: vars.font.body,
  whiteSpace: "nowrap",
});

// The unapplied row's "+ Add" pill, right-aligned on the primary line.
const addBtnStyle = (hovered: boolean): CSSProperties => ({
  marginLeft: "auto",
  fontSize: vars.font.size2xs,
  fontWeight: vars.font.weightMedium,
  padding: "5px 12px",
  borderRadius: "6px",
  border: `1px solid ${vars.color.border}`,
  background: "transparent",
  color: hovered ? vars.color.text : vars.color.textMuted,
  cursor: "pointer",
  fontFamily: vars.font.body,
  whiteSpace: "nowrap" as const,
  flexShrink: 0,
});

// "Remove" — one of the applied row's second-line actions, danger-colored throughout.
const removeBtnStyle = (hovered: boolean): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  fontSize: vars.font.size2xs,
  fontWeight: vars.font.weightMedium,
  padding: "5px 10px",
  borderRadius: "7px",
  border: "1px solid rgba(239, 68, 68, 0.35)",
  background: hovered ? "rgba(239, 68, 68, 0.12)" : "transparent",
  color: vars.color.danger,
  cursor: "pointer",
  flexShrink: 0,
  fontFamily: vars.font.body,
  whiteSpace: "nowrap",
});

// The remove-confirm dialog's body text — what will actually happen to this trip's
// rows, from the same counts the whole-template delete dialog shows (Doc 07 §5).
const removePreviewText = (preview: TemplateDeletePreview): string => {
  const parts: string[] = [];
  if (preview.removes > 0) parts.push(`${preview.removes} item${preview.removes !== 1 ? "s" : ""} will be removed from this trip`);
  if (preview.keptViaOtherSource > 0)
    parts.push(`${preview.keptViaOtherSource} will stay — they were either modified or are part of another template`);
  if (preview.keptEdited > 0) parts.push(`${preview.keptEdited} will stay — you've edited ${preview.keptEdited !== 1 ? "them" : "it"}`);
  if (parts.length === 0) return "No items on this trip came from this template — only the applied status will be cleared.";
  return parts.join("; ") + ".";
};

// ---------------------------------------------------------------------------
// Template row component
// ---------------------------------------------------------------------------

interface TemplateRowProps {
  template: TemplateSummary;
  applied: boolean;
  onToggle: (templateId: TemplateId) => void;
  onPreview: (template: TemplateSummary) => void;
  // Opens the confirm dialog rather than removing outright — separate from `onToggle`
  // (which the "+ Add" button still uses directly) so Remove's blast radius is shown
  // before anything happens.
  onRemove: (template: TemplateSummary) => void;
  // T28 §4.1 — present only for an applied template this user may actually write
  // (Doc 13 §7); the page opens sync mode over the trip list.
  onSync?: (templateId: TemplateId) => void;
  // Doc 14 §1 — the other direction, offered for every applied template the user can
  // still read (no template-write requirement, hence a separate prop from `onSync`).
  onRefresh?: (templateId: TemplateId) => void;
}

// Issue: an applied template used to render Refresh/Sync/Preview/Applied all on one
// line — at the modal's width that crowded past the name and visibly overlapped it.
// An applied row now gets a distinct two-line layout: name + preview on top, and
// Refresh/Sync/Remove on their own line underneath, indented under the name.
const TemplateRow = ({ template, applied, onToggle, onPreview, onRemove, onSync, onRefresh }: TemplateRowProps): ReactNode => {
  const [rowHovered, setRowHovered] = useState(false);
  const [previewHovered, setPreviewHovered] = useState(false);
  const [addHovered, setAddHovered] = useState(false);
  const [syncHovered, setSyncHovered] = useState(false);
  const [refreshHovered, setRefreshHovered] = useState(false);
  const [removeHovered, setRemoveHovered] = useState(false);

  return (
    <div
      style={rowStyle(rowHovered)}
      onMouseEnter={() => setRowHovered(true)}
      onMouseLeave={() => setRowHovered(false)}
    >
      <div style={primaryLineStyle}>
        <div style={iconStyle(template)} aria-hidden>
          {template.emoji ?? TEMPLATE_EMOJIS[0]}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={nameStyle}>{template.name}</div>
        </div>
        <button
          type="button"
          style={previewBtnStyle(previewHovered)}
          aria-label={`Preview ${template.name}`}
          title="Preview contents"
          onMouseEnter={() => setPreviewHovered(true)}
          onMouseLeave={() => setPreviewHovered(false)}
          onClick={() => onPreview(template)}
        >
          {EyeIcon}
        </button>
        {!applied && (
          <button
            type="button"
            style={addBtnStyle(addHovered)}
            aria-label={`Add ${template.name}`}
            aria-pressed={applied}
            onMouseEnter={() => setAddHovered(true)}
            onMouseLeave={() => setAddHovered(false)}
            onClick={() => onToggle(template.id)}
          >
            + Add
          </button>
        )}
      </div>
      {applied && (
        <div style={secondaryLineStyle}>
          {onRefresh !== undefined && (
            <button
              type="button"
              style={syncBtnStyle(refreshHovered)}
              aria-label={`Refresh ${template.name} into this trip`}
              title="Bring this template's newer items into the trip"
              onMouseEnter={() => setRefreshHovered(true)}
              onMouseLeave={() => setRefreshHovered(false)}
              onClick={() => onRefresh(template.id)}
            >
              {RefreshDownIcon}
              Refresh…
            </button>
          )}
          {onSync !== undefined && (
            <button
              type="button"
              style={syncBtnStyle(syncHovered)}
              aria-label={`Sync back to template: ${template.name}`}
              title="Push this trip's changes back into the template"
              onMouseEnter={() => setSyncHovered(true)}
              onMouseLeave={() => setSyncHovered(false)}
              onClick={() => onSync(template.id)}
            >
              {SyncIcon}
              Sync back to template…
            </button>
          )}
          <button
            type="button"
            style={removeBtnStyle(removeHovered)}
            aria-label={`Remove ${template.name}`}
            aria-pressed={applied}
            onMouseEnter={() => setRemoveHovered(true)}
            onMouseLeave={() => setRemoveHovered(false)}
            onClick={() => onRemove(template)}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Ghost row — an applied template whose document has been deleted. Its rows and
// this trip's `importedTemplateIds` entry both survive the delete untouched, so the
// trip should keep saying "a template was part of this trip"; the only action left
// is Remove, which the confirm dialog gates with an extra warning since a deleted
// template can never be re-added.
// ---------------------------------------------------------------------------

interface GhostTemplateRowProps {
  name: string;
  onRemove: () => void;
}

const GhostTemplateRow = ({ name, onRemove }: GhostTemplateRowProps): ReactNode => {
  const [rowHovered, setRowHovered] = useState(false);
  const [removeHovered, setRemoveHovered] = useState(false);

  return (
    <div
      style={rowStyle(rowHovered)}
      onMouseEnter={() => setRowHovered(true)}
      onMouseLeave={() => setRowHovered(false)}
    >
      <div style={primaryLineStyle}>
        <div style={ghostIconStyle} aria-hidden>
          {TrashIcon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={nameStyle}>{name}</div>
          <div style={ghostSublabelStyle}>Template deleted</div>
        </div>
      </div>
      <div style={secondaryLineStyle}>
        <button
          type="button"
          style={removeBtnStyle(removeHovered)}
          aria-label={`Remove ${name}`}
          onMouseEnter={() => setRemoveHovered(true)}
          onMouseLeave={() => setRemoveHovered(false)}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Preview modal
// ---------------------------------------------------------------------------

interface TemplatePreviewModalProps {
  template: TemplateSummary | null;
  // Loaded via fetchTemplateContents when the preview opens; null while loading.
  contents: TemplateInput | null;
  applied: boolean;
  onClose: () => void;
  onAdd: (templateId: TemplateId) => void;
}

const TemplatePreviewModal = ({ template, contents, applied, onClose, onAdd }: TemplatePreviewModalProps): ReactNode => {
  if (template === null) return null;

  return (
    <Dialog
      open={template !== null}
      onClose={onClose}
      title={template.name}
      footer={
        <div style={{ display: "flex", gap: vars.space.sm }}>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
          {!applied && (
            <Button
              size="sm"
              onClick={() => {
                onAdd(template.id);
                onClose();
              }}
            >
              + Add this template
            </Button>
          )}
        </div>
      }
    >
      <div>
        {/* Icon row */}
        <div style={{ display: "flex", alignItems: "center", gap: vars.space.sm, marginBottom: vars.space.md }}>
          <div style={iconStyle(template)} aria-hidden>
            {template.emoji ?? TEMPLATE_EMOJIS[0]}
          </div>
          <span style={{ color: vars.color.textMuted, fontSize: vars.font.sizeSm }}>template</span>
        </div>
        {contents === null ? (
          <div
            style={{
              marginTop: vars.space.lg,
              padding: vars.space.md,
              color: vars.color.textMuted,
              fontSize: vars.font.sizeSm,
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Loading contents…
          </div>
        ) : (
          <div style={{ marginTop: vars.space.md }}>
            {contents.categories.length === 0 && (
              <div style={{ color: vars.color.textMuted, fontSize: vars.font.sizeSm, fontStyle: "italic" }}>
                This template is empty.
              </div>
            )}
            {contents.categories.map((cat) => {
              const catItems = contents.items.filter((i) => i.categoryId === cat.id);
              const catTodos = contents.todos.filter((t) => t.categoryId === cat.id);
              return (
                <div key={cat.id} style={{ marginBottom: vars.space.md }}>
                  <div style={{ fontSize: vars.font.sizeSm, fontWeight: vars.font.weightMedium, color: vars.color.text }}>
                    {cat.name}
                    <span style={{ color: vars.color.textMuted, fontWeight: vars.font.weightRegular, marginLeft: "6px" }}>
                      {catItems.length + catTodos.length}
                    </span>
                  </div>
                  <ul style={{ margin: `4px 0 0`, paddingLeft: vars.space.lg, color: vars.color.textMuted, fontSize: vars.font.sizeXs }}>
                    {[...catItems.map((i) => i.name), ...catTodos.map((t) => t.name)].map((name, idx) => (
                      <li key={idx}>{name}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main modal
// ---------------------------------------------------------------------------

export interface TemplatesForTripModalProps {
  open: boolean;
  onClose: () => void;
  trip: Trip;
  // The page's trip-detail store — the live trip contents the import dedupes against
  // (Doc 07 §3: the picker never re-reads the repositories itself).
  detail: ReturnType<typeof createTripDetailStore>;
  // T28 §4.1 — enter sync mode for this template. Offered per applied-template row, but
  // only for the ones `syncCandidates` says the user can write (Doc 13 §7).
  onSyncTemplate?: (templateId: TemplateId) => void;
}

export const TemplatesForTripModal = ({ open, onClose, trip, detail, onSyncTemplate }: TemplatesForTripModalProps): ReactNode => {
  const { home } = useSession();
  const { repos } = useSessionDeps();
  const { auth } = useStores();
  const actor = useStoreValue(auth, (s) => s.user?.id ?? null);
  const allTemplates = useStoreValue(home, (s) => s.templates);

  const [query, setQuery] = useState("");
  const [previewTemplate, setPreviewTemplate] = useState<TemplateSummary | null>(null);
  const [previewContents, setPreviewContents] = useState<TemplateInput | null>(null);
  // A re-import awaiting explicit confirmation (Doc 07 §3: "already imported — add again?").
  const [confirmReimport, setConfirmReimport] = useState<TemplateInput | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // A "Remove" awaiting explicit confirmation — the counts below make the blast radius
  // (what actually gets deleted vs. kept) visible before anything happens. `gone` marks
  // a ghost row (the template doc no longer exists): removing it can't be undone by
  // re-adding, since there is nothing left to re-import.
  const [confirmRemove, setConfirmRemove] = useState<{ id: TemplateId; name: string; gone: boolean } | null>(null);

  const openPreview = (tpl: TemplateSummary): void => {
    setPreviewTemplate(tpl);
    setPreviewContents(null);
    void repos.templates.fetchTemplateContents(tpl.id).then((r) => {
      if (r.ok) setPreviewContents(r.value);
    });
  };

  // The plan applier is a composition-root seam, not part of `AllRepositories`: the
  // in-memory bundle exposes it (fake mode applies the batch locally); the Firestore
  // backend's batched apply lands with the T12 backend work, so absence = no-op.
  const importPicker = useMemo(
    () =>
      createImportPickerStore({
        // Non-null in practice: this modal only renders from TripDetailPage, which
        // itself doesn't render until useTripDetail resolves the signed-in actor.
        actor: actor as UserId,
        applyImportPlan: (plan, tripId) => {
          const seam = repos as AllRepositories & { applyImportPlan?: (p: ImportPlan, t?: TripId) => void };
          seam.applyImportPlan?.(plan, tripId);
        },
      }),
    [repos, actor],
  );

  // Doc 14 — the refresh dialog's state. Same applier seam as the import: a refresh
  // plan *is* an import plan plus in-place updates.
  const refresh = useMemo(
    () =>
      createTemplateRefreshStore({
        templates: repos.templates,
        actor: actor as UserId,
        applyRefreshPlan: (plan, tripId) => {
          const seam = repos as AllRepositories & { applyImportPlan?: (p: ImportPlan, t?: TripId) => void };
          seam.applyImportPlan?.(plan, tripId);
        },
      }),
    [repos, actor],
  );
  const refreshState = useStoreValue(refresh, (s) => s);

  const q = query.trim().toLowerCase();
  const filteredTemplates = q === "" ? allTemplates : allTemplates.filter((t) => t.name.toLowerCase().includes(q));
  // Applying a template moves it to the top of the list (a stable partition — order
  // within each group is otherwise unchanged) so it's easy to find its Refresh/Sync/
  // Remove actions right after adding it, instead of hunting back through the list.
  const visibleTemplates = [...filteredTemplates].sort(
    (a, b) => Number(!trip.importedTemplateIds.includes(a.id)) - Number(!trip.importedTemplateIds.includes(b.id)),
  );

  const appliedCount = allTemplates.filter((t) => trip.importedTemplateIds.includes(t.id)).length;

  // Applied ids with no matching TemplateSummary — the template was deleted, but this
  // trip's own items/todos and its importedTemplateIds entry both survive untouched
  // (Doc 07 §5, revised), so the trip should keep showing it was part of the trip.
  const ghostIds = useMemo(
    () => trip.importedTemplateIds.filter((id) => !allTemplates.some((t) => t.id === id)),
    [trip.importedTemplateIds, allTemplates],
  );

  // Doc 13 §7 — imported ∩ mine ∩ unarchived ∩ owner/editor. The same pure filter the
  // sync store applies, so the action never appears where the write would be denied.
  const syncable = useMemo(
    () => new Set(syncCandidates(trip, allTemplates).map((t) => t.id as string)),
    [trip, allTemplates],
  );

  // Doc 14 §7 — imported ∩ visible. No role test: a refresh only reads the template.
  const refreshable = useMemo(
    () => new Set(refreshCandidates(trip, allTemplates).map((t) => t.id as string)),
    [trip, allTemplates],
  );

  const openRefresh = (templateId: TemplateId): void => {
    const summary = allTemplates.find((t) => t.id === templateId);
    if (summary === undefined) return;
    const s = detail.getState();
    void refresh
      .getState()
      .openFor(trip, { categories: s.categories, items: s.items, todos: s.todos }, summary);
  };

  // Fetch the template's contents, then run the dedupe planner as one optimistic
  // batch (Doc 07 §3, Doc 05 §2). A re-import is gated behind confirmation.
  const runImport = async (input: TemplateInput, confirm: boolean): Promise<void> => {
    const s = detail.getState();
    const current = { categories: s.categories, items: s.items, todos: s.todos };
    const r = await importPicker.getState().importInto(trip, current, [input], { confirm });
    if (r.ok) {
      setConfirmReimport(null);
      setImportError(null);
      return;
    }
    if (importPicker.getState().needsConfirm) {
      setConfirmReimport(input);
    } else {
      setImportError("Could not apply the template.");
    }
  };

  // Issue #180 — imported rows are auto-tagged with the source template's name. The
  // planner records it on `provenance.templateSources`; no name means no tag.
  const handleApply = (templateId: TemplateId): void => {
    void (async () => {
      const fetched = await repos.templates.fetchTemplateContents(templateId);
      if (!fetched.ok) {
        setImportError("Could not load the template's contents.");
        return;
      }
      const name = allTemplates.find((t) => t.id === templateId)?.name;
      await runImport(name !== undefined ? { ...fetched.value, name } : fetched.value, false);
    })();
  };

  // This trip's rows sourced from `templateId` — the row set both the confirm dialog's
  // preview counts and the actual removal plan below are computed from, so what the
  // user is shown before confirming is exactly what gets acted on.
  const rowsFor = (templateId: TemplateId): AssociatedRow[] => {
    const s = detail.getState();
    return [
      ...s.items.filter((i) => i.templateIds?.includes(templateId)).map((item) => ({ collection: "items" as const, item })),
      ...s.todos.filter((t) => t.templateIds?.includes(templateId)).map((item) => ({ collection: "todos" as const, item })),
    ];
  };

  // The deleted template's name, recovered from a snapshot any surviving row still
  // carries (provenance.templateSources[].templateName) — never a live lookup.
  const ghostName = (id: TemplateId): string => {
    const row = rowsFor(id)[0];
    return row?.item.provenance?.templateSources.find((s) => s.templateId === id)?.templateName ?? "Deleted template";
  };
  const visibleGhostIds = q === "" ? ghostIds : ghostIds.filter((id) => ghostName(id).toLowerCase().includes(q));

  // Detaches this template from just this trip (Doc 07 §5's decision table, scoped to
  // the trip's own rows): a row still claimed by another template or the user's own
  // edit is kept, an unmodified orphan is deleted, an edited orphan becomes independent.
  const handleRemove = (templateId: TemplateId): void => {
    const plan = planTemplateRemoval(trip, templateId, "removeUnchangedOnly", rowsFor(templateId));
    const seam = repos as AllRepositories & { applyTemplateRemovalPlan?: (p: TemplateRemovalPlan, t: TripId) => void };
    seam.applyTemplateRemovalPlan?.(plan, trip.id);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title="Templates for this trip"
        subtitle="Apply a template to add its items, or remove one to take its items off the list."
        footer={
          <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}>
            <span style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted }}>
              <strong>{appliedCount + ghostIds.length}</strong> of {allTemplates.length + ghostIds.length} templates applied
            </span>
            <Button size="sm" onClick={onClose}>
              Done
            </Button>
          </div>
        }
      >
        {/* Search box */}
        <div style={searchWrapStyle}>
          <div style={searchBoxStyle}>
            {SearchIcon()}
            <input
              type="text"
              placeholder="Search templates…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={searchInputStyle}
              aria-label="Search templates"
            />
          </div>
        </div>

        {/* Template list */}
        <div style={{ marginTop: vars.space.sm }}>
          {visibleGhostIds.length === 0 && visibleTemplates.length === 0 ? (
            <div style={emptyStyle}>No templates match your search.</div>
          ) : (
            <>
              {/* Ghost rows first — they're applied, same as the top of visibleTemplates. */}
              {visibleGhostIds.map((id) => (
                <GhostTemplateRow
                  key={id}
                  name={ghostName(id)}
                  onRemove={() => setConfirmRemove({ id, name: ghostName(id), gone: true })}
                />
              ))}
              {visibleGhostIds.length > 0 && visibleTemplates.length > 0 && <hr style={dividerStyle} />}
              {visibleTemplates.map((t, idx) => {
                const applied = trip.importedTemplateIds.includes(t.id);
                // A rule between the "already added" group and the "available to add"
                // group below it — only at the boundary (visibleTemplates is sorted
                // applied-first), so it's a no-op when either group is empty.
                const prevApplied = idx > 0 && trip.importedTemplateIds.includes(visibleTemplates[idx - 1]!.id);
                const showDivider = idx > 0 && prevApplied && !applied;
                return (
                  <div key={t.id}>
                    {showDivider && <hr style={dividerStyle} />}
                    <TemplateRow
                      template={t}
                      applied={applied}
                      onToggle={handleApply}
                      onPreview={openPreview}
                      onRemove={(tpl) => setConfirmRemove({ id: tpl.id, name: tpl.name, gone: false })}
                      {...(onSyncTemplate !== undefined && syncable.has(t.id) ? { onSync: onSyncTemplate } : {})}
                      {...(applied && refreshable.has(t.id) ? { onRefresh: openRefresh } : {})}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>
      </Dialog>

      {/* Doc 14 — the refresh checklist for one applied template. */}
      {refreshState.open && (
        <RefreshTemplateDialog
          templateName={refreshState.templateName}
          entries={refreshState.draft?.entries ?? []}
          checked={refreshState.checked}
          counts={refresh.getState().counts()}
          loading={refreshState.loading}
          error={refreshState.error}
          onToggle={(key) => refresh.getState().toggle(key)}
          onConfirm={() => refresh.getState().confirm()}
          onClose={() => refresh.getState().cancel()}
        />
      )}

      {/* Preview modal (secondary) */}
      <TemplatePreviewModal
        template={previewTemplate}
        contents={previewContents}
        applied={previewTemplate !== null && trip.importedTemplateIds.includes(previewTemplate.id)}
        onClose={() => setPreviewTemplate(null)}
        onAdd={handleApply}
      />

      {/* Re-import confirmation (Doc 07 §3: dedupe makes re-import idempotent for
          unchanged items, but the UI still confirms to avoid surprise). */}
      <Dialog
        open={confirmReimport !== null}
        onClose={() => setConfirmReimport(null)}
        title="Import again?"
        subtitle="This template is already on the trip."
        footer={
          <div style={{ display: "flex", gap: vars.space.sm }}>
            <Button variant="ghost" size="sm" onClick={() => setConfirmReimport(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (confirmReimport !== null) void runImport(confirmReimport, true);
              }}
            >
              Import again
            </Button>
          </div>
        }
      >
        <p style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, margin: 0 }}>
          Items already on the list are merged, not duplicated — only new or changed
          template entries will be added.
        </p>
      </Dialog>

      {/* Remove confirmation — the counts make the blast radius explicit before anything
          happens: an unmodified row that only this template ever claimed is deleted; a
          row still claimed by another template, or one the user edited, is kept. */}
      <Dialog
        open={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
        title={confirmRemove !== null ? `Remove "${confirmRemove.name}"?` : ""}
        subtitle="This only affects this trip — the template itself is untouched."
        footer={
          <div style={{ display: "flex", gap: vars.space.sm }}>
            <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                if (confirmRemove !== null) {
                  handleRemove(confirmRemove.id);
                  setConfirmRemove(null);
                }
              }}
            >
              Remove
            </Button>
          </div>
        }
      >
        {confirmRemove?.gone === true && (
          <p style={{ fontSize: vars.font.sizeSm, color: vars.color.danger, fontWeight: vars.font.weightMedium, margin: `0 0 ${vars.space.sm}` }}>
            This template has been deleted — once removed, this trip can't get it back.
          </p>
        )}
        <p style={{ fontSize: vars.font.sizeSm, color: vars.color.textMuted, margin: 0 }}>
          {confirmRemove !== null && removePreviewText(previewTemplateDelete(confirmRemove.id, "removeUnchangedOnly", rowsFor(confirmRemove.id)))}
        </p>
      </Dialog>

      {importError !== null && (
        <div role="alert" style={{ position: "fixed", bottom: vars.space.lg, left: "50%", transform: "translateX(-50%)", zIndex: 100 }}>
          <span
            style={{
              background: vars.color.surface,
              border: `1px solid ${vars.color.danger}`,
              color: vars.color.danger,
              borderRadius: vars.radius.md,
              padding: `${vars.space.sm} ${vars.space.md}`,
              fontSize: vars.font.sizeSm,
            }}
          >
            {importError}
          </span>
        </div>
      )}
    </>
  );
};
